/**
 * Labstep
 *
 * @module state/slices/globalSearch
 * @desc Global search Redux slice
 */

import {
  createSelector,
  createSlice,
  type PayloadAction,
  type SliceCaseReducers,
} from '@reduxjs/toolkit';
import { GlobalSearchResult } from 'labstep-web/models/global-search-result.model';
import type {
  GlobalSearchState,
  SetErrorPayload,
  SetIsOpenPayload,
  SetIsSearchingPayload,
  SetSearchQueryPayload,
  SetSearchResultsPayload,
} from './types';

const initialState: GlobalSearchState = {
  query: '',
  results: [],
  isSearching: false,
  isOpen: false,
  error: null,
  hasSearched: false,
};

export const globalSearchSlice = createSlice<
  GlobalSearchState,
  SliceCaseReducers<GlobalSearchState>
>({
  name: 'globalSearch',
  initialState,
  reducers: {
    setSearchQuery: (
      state,
      action: PayloadAction<SetSearchQueryPayload>,
    ) => {
      state.query = action.payload.query;
      if (action.payload.query === '') {
        state.results = [];
        state.hasSearched = false;
        state.error = null;
      }
    },
    setSearchResults: (
      state,
      action: PayloadAction<SetSearchResultsPayload>,
    ) => {
      state.results = action.payload.results;
      state.isSearching = false;
      state.hasSearched = true;
      state.error = null;
    },
    setIsSearching: (
      state,
      action: PayloadAction<SetIsSearchingPayload>,
    ) => {
      state.isSearching = action.payload.isSearching;
      if (action.payload.isSearching) {
        state.error = null;
      }
    },
    setIsOpen: (state, action: PayloadAction<SetIsOpenPayload>) => {
      state.isOpen = action.payload.isOpen;
      if (!action.payload.isOpen) {
        // Reset search when closing
        state.query = '';
        state.results = [];
        state.hasSearched = false;
        state.error = null;
        state.isSearching = false;
      }
    },
    setError: (state, action: PayloadAction<SetErrorPayload>) => {
      state.error = action.payload.error;
      state.isSearching = false;
    },
    clearSearch: (state) => {
      state.query = '';
      state.results = [];
      state.hasSearched = false;
      state.error = null;
      state.isSearching = false;
    },
  },
});

// Base selector
const selectGlobalSearchState = (state: any) => state.globalSearch;

// Selectors
export const selectSearchQuery = createSelector(
  [selectGlobalSearchState],
  (globalSearch) => globalSearch.query,
);

export const selectSearchResults = createSelector(
  [selectGlobalSearchState],
  (globalSearch) =>
    globalSearch.results.map(
      (result: any) => new GlobalSearchResult(result),
    ),
);

export const selectIsSearching = createSelector(
  [selectGlobalSearchState],
  (globalSearch) => globalSearch.isSearching,
);

export const selectIsSearchOpen = createSelector(
  [selectGlobalSearchState],
  (globalSearch) => globalSearch.isOpen,
);

export const selectSearchError = createSelector(
  [selectGlobalSearchState],
  (globalSearch) => globalSearch.error,
);

export const selectHasSearched = createSelector(
  [selectGlobalSearchState],
  (globalSearch) => globalSearch.hasSearched,
);

// Action creators
export const {
  setSearchQuery,
  setSearchResults,
  setIsSearching,
  setIsOpen,
  setError,
  clearSearch,
} = globalSearchSlice.actions;

export default globalSearchSlice.reducer;
